import { MathUtils, Vector3 } from "three/webgpu";
import { composeWorldPositionFromCartography, decomposeWorldPositionToCartography } from "../config/worldFrame";
import type { PresentedNode } from "../../presentation/mesh";

const ORBITAL_ANGLE_AMPLITUDE_MIN = MathUtils.degToRad(2.8);
const ORBITAL_ANGLE_AMPLITUDE_MAX = MathUtils.degToRad(4.6);
const ORBITAL_PRECESSION_AMPLITUDE_MIN = MathUtils.degToRad(0.5);
const ORBITAL_PRECESSION_AMPLITUDE_MAX = MathUtils.degToRad(1.0);
const ORBITAL_RADIAL_BREATH_RATIO_MIN = 0.009;
const ORBITAL_RADIAL_BREATH_RATIO_MAX = 0.018;
const ORBITAL_DEPTH_WOBBLE_MIN = 0.010;
const ORBITAL_DEPTH_WOBBLE_MAX = 0.032;
const ORBITAL_ANGULAR_SPEED_MIN = 0.17;
const ORBITAL_ANGULAR_SPEED_MAX = 0.26;
const ORBITAL_PRECESSION_SPEED_MIN = 0.070;
const ORBITAL_PRECESSION_SPEED_MAX = 0.115;
const ORBITAL_RADIAL_SPEED_MIN = 0.115;
const ORBITAL_RADIAL_SPEED_MAX = 0.180;
const ORBITAL_DEPTH_SPEED_MIN = 0.080;
const ORBITAL_DEPTH_SPEED_MAX = 0.130;

export type AnchoredOrbitDrift = {
  readonly basePosition: Vector3;
  readonly cartography: Vector3;
  readonly baseAngle: number;
  readonly baseRadius: number;
  readonly baseDepth: number;
  readonly angularAmplitude: number;
  readonly precessionAmplitude: number;
  readonly radialAmplitude: number;
  readonly depthAmplitude: number;
  readonly angularSpeed: number;
  readonly precessionSpeed: number;
  readonly radialSpeed: number;
  readonly depthSpeed: number;
  readonly angularPhase: number;
  readonly precessionPhase: number;
  readonly radialPhase: number;
  readonly depthPhase: number;
};

function hash01(seed: string, salt: number) {
  let hash = (2166136261 ^ salt) >>> 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash / 4294967295;
}

function range(seed: string, salt: number, min: number, max: number) {
  return MathUtils.lerp(min, max, hash01(seed, salt));
}

function motionTemperature(node: PresentedNode) {
  if (node.kind === "system") return 0.82;
  if (node.kind === "goal") return 0.88;
  if (node.kind === "location") return 0.74;
  if (node.kind === "artifact") return 0.92;
  return 1;
}

function anchoredWave(time: number, speed: number, phase: number) {
  const primary = Math.sin(time * speed);
  const secondary = Math.sin(time * speed * 0.61 + phase) - Math.sin(phase);
  return primary * 0.72 + secondary * 0.28;
}

export function createAnchoredOrbitDrift(node: PresentedNode, basePosition: Vector3) {
  const cartography = decomposeWorldPositionToCartography(basePosition, new Vector3());
  const baseRadius = Math.max(Math.hypot(cartography.x, cartography.y), 0.001);
  const temperature = motionTemperature(node);
  const angularAmplitude =
    range(node.id, 11, ORBITAL_ANGLE_AMPLITUDE_MIN, ORBITAL_ANGLE_AMPLITUDE_MAX) * temperature;
  const precessionAmplitude =
    range(node.id, 17, ORBITAL_PRECESSION_AMPLITUDE_MIN, ORBITAL_PRECESSION_AMPLITUDE_MAX) * temperature;
  const radialAmplitude = Math.max(
    0.028,
    baseRadius * range(node.id, 23, ORBITAL_RADIAL_BREATH_RATIO_MIN, ORBITAL_RADIAL_BREATH_RATIO_MAX) * temperature,
  );
  const depthAmplitude = range(node.id, 29, ORBITAL_DEPTH_WOBBLE_MIN, ORBITAL_DEPTH_WOBBLE_MAX) * temperature;
  const cadence = 1 / Math.max(node.magnitude, 0.72);
  return {
    basePosition: basePosition.clone(),
    cartography,
    baseAngle: Math.atan2(cartography.y, cartography.x),
    baseRadius,
    baseDepth: cartography.z,
    angularAmplitude,
    precessionAmplitude,
    radialAmplitude,
    depthAmplitude,
    angularSpeed: range(node.id, 31, ORBITAL_ANGULAR_SPEED_MIN, ORBITAL_ANGULAR_SPEED_MAX) * cadence,
    precessionSpeed: range(node.id, 37, ORBITAL_PRECESSION_SPEED_MIN, ORBITAL_PRECESSION_SPEED_MAX) * cadence,
    radialSpeed: range(node.id, 41, ORBITAL_RADIAL_SPEED_MIN, ORBITAL_RADIAL_SPEED_MAX) * cadence,
    depthSpeed: range(node.id, 43, ORBITAL_DEPTH_SPEED_MIN, ORBITAL_DEPTH_SPEED_MAX) * cadence,
    angularPhase: range(node.id, 47, 0, Math.PI * 2),
    precessionPhase: range(node.id, 53, 0, Math.PI * 2),
    radialPhase: range(node.id, 59, 0, Math.PI * 2),
    depthPhase: range(node.id, 61, 0, Math.PI * 2),
  } satisfies AnchoredOrbitDrift;
}

export function sampleAnchoredOrbitDrift(drift: AnchoredOrbitDrift, phaseTime: number, target: Vector3) {
  if (phaseTime <= 0.000001) return target.copy(drift.basePosition);
  const angularOffset = anchoredWave(phaseTime, drift.angularSpeed, drift.angularPhase) * drift.angularAmplitude;
  const precessionOffset =
    anchoredWave(phaseTime, drift.precessionSpeed, drift.precessionPhase) * drift.precessionAmplitude;
  const radius = drift.baseRadius + anchoredWave(phaseTime, drift.radialSpeed, drift.radialPhase) * drift.radialAmplitude;
  const depth = drift.baseDepth + anchoredWave(phaseTime, drift.depthSpeed, drift.depthPhase) * drift.depthAmplitude;
  const angle = drift.baseAngle + angularOffset + precessionOffset;
  return composeWorldPositionFromCartography(Math.cos(angle) * radius, Math.sin(angle) * radius, depth, target);
}
